import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CheckBoxWithLabel } from "../../components/shared/InputField";
import {
  HStack,
  isCompanySettingsReadOnly,
  PrimaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../components/utils";
import { useGetCustomFields } from "../../queries/customFields";
import {
  useGetEmployeeHistorySettings,
  useSetEmployeeHistorySettings,
} from "../../queries/siteSettings";
import { UdfEntityType } from "../../types/CustomFields";

const STANDARD_FIELDS = [
  ["name", "Employee name"],
  ["email", "Work email"],
  ["hrId", "Employee ID"],
  ["type", "Employee type"],
  ["dateOfJoining", "Date of joining"],
  ["subsidiaryCompanyId", "Organization"],
  ["legalName", "Legal name"],
  ["personalEmail", "Personal email"],
  ["designation", "Designation"],
  ["department", "Department"],
  ["contact", "Contact number"],
  ["address", "Address"],
  ["residentialStatus", "Country of residence"],
  ["nationality", "Nationality"],
  ["note", "Notes"],
  ["compensationDetails.grade", "Grade"],
  ["compensationDetails.salary", "Salary"],
  ["managerIds", "Managers"],
  ["additionalMetaData.tags", "Tags"],
] as const;

function sameSelection(left: string[], right: string[]) {
  return (
    left.length === right.length &&
    left.every((fieldPath) => right.includes(fieldPath))
  );
}

function FieldChoices({
  fields,
  selected,
  disabled,
  onChange,
}: {
  fields: { path: string; label: string }[];
  selected: string[];
  disabled: boolean;
  onChange: (path: string, checked: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
      {fields.map((field) => (
        <CheckBoxWithLabel
          key={field.path}
          id={`internal-transfer-${field.path}`}
          label={field.label}
          labelclassname="ml-2 text-sm text-primaryText"
          className="h-4 w-4 accent-primary"
          checked={selected.includes(field.path)}
          disabled={disabled}
          onChange={(event) => onChange(field.path, event.target.checked)}
        />
      ))}
    </div>
  );
}

function InternalTransferSettings() {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const historySettings = useGetEmployeeHistorySettings();
  const customFields = useGetCustomFields(UdfEntityType.EMPLOYEE);
  const saveSettings = useSetEmployeeHistorySettings();
  const [selected, setSelected] = useState<string[]>([]);
  const [savedSelection, setSavedSelection] = useState<string[]>([]);
  const standardFields = useMemo(
    () => STANDARD_FIELDS.map(([path, label]) => ({ path, label })),
    []
  );
  const customFieldOptions = useMemo(
    () =>
      (customFields.data || []).map((field) => ({
        path: `additionalMetaData.udfValues.${field.fieldPath}`,
        label: field.name,
      })),
    [customFields.data]
  );

  useEffect(() => {
    const configured = historySettings.data?.internalTransferFieldPaths || [];
    setSelected(configured);
    setSavedSelection(configured);
  }, [historySettings.data?.internalTransferFieldPaths]);

  const updateSelection = (path: string, checked: boolean) => {
    setSelected((current) =>
      checked
        ? [...new Set([...current, path])]
        : current.filter((fieldPath) => fieldPath !== path)
    );
  };
  const isDirty = !sameSelection(selected, savedSelection);

  const saveChanges = () => {
    saveSettings.mutate(
      { internalTransferFieldPaths: selected },
      {
        onSuccess: () => {
          setSavedSelection(selected);
          toast("Internal transfer settings saved", { type: "success" });
        },
        onError: () => {
          toast("We couldn't save internal transfer settings", {
            type: "error",
          });
        },
      }
    );
  };

  return (
    <VStack className="w-full rounded-md bg-white">
      <div className="border-b border-borderColor px-6 py-4">
        <h2 className="text-base font-semibold text-headerColor">
          Internal transfer settings
        </h2>
        <p className="mt-1 text-sm text-secondaryText">
          Select the profile fields that should classify an employee edit as an
          internal transfer.
        </p>
      </div>

      <VStack className="max-w-4xl gap-6 px-6 py-6">
        <VStack className="gap-1">
          <h3 className="text-sm font-semibold text-headerColor">
            Transfer trigger fields
          </h3>
          <p className="text-sm text-secondaryText">
            When any selected field changes, the complete save is recorded as an
            internal transfer in the Activity Log.
          </p>
        </VStack>

        <section aria-labelledby="standard-transfer-fields">
          <h3
            id="standard-transfer-fields"
            className="mb-3 text-xs2 font-semibold text-primaryText"
          >
            Standard profile fields
          </h3>
          <FieldChoices
            fields={standardFields}
            selected={selected}
            disabled={
              isUserReadOnly ||
              historySettings.isLoading ||
              saveSettings.isLoading
            }
            onChange={updateSelection}
          />
        </section>

        {customFieldOptions.length > 0 && (
          <section aria-labelledby="custom-transfer-fields">
            <h3
              id="custom-transfer-fields"
              className="mb-3 text-xs2 font-semibold text-primaryText"
            >
              Custom employee fields
            </h3>
            <FieldChoices
              fields={customFieldOptions}
              selected={selected}
              disabled={
                isUserReadOnly ||
                historySettings.isLoading ||
                saveSettings.isLoading
              }
              onChange={updateSelection}
            />
          </section>
        )}
      </VStack>

      <HStack className="items-center justify-between gap-3 border-t border-borderColor px-6 py-4">
        <TertiaryCTAButton
          type="button"
          onClick={() => setSelected(savedSelection)}
          disabled={isUserReadOnly || !isDirty || saveSettings.isLoading}
        >
          Discard changes
        </TertiaryCTAButton>
        <PrimaryCTAButton
          type="button"
          onClick={saveChanges}
          disabled={isUserReadOnly || !isDirty || saveSettings.isLoading}
        >
          {saveSettings.isLoading ? "Saving changes" : "Save changes"}
        </PrimaryCTAButton>
      </HStack>
    </VStack>
  );
}

export default InternalTransferSettings;
